import React, { useState } from "react";
import { motion } from "framer-motion";

// Questions for user input
const questions = [
  {
    label: "What are your main interests or skills?",
    name: "skills",
    placeholder: "e.g., cooking, coding, teaching, design",
  },
  {
    label: "How much capital can you invest?",
    name: "capital",
    placeholder: "e.g., Low, Medium, High",
  },
  {
    label: "Preferred business type?",
    name: "type",
    placeholder: "e.g., Online, Offline, Service, Product",
  },
  {
    label: "Target audience/location?",
    name: "audience",
    placeholder: "e.g., Students in Mumbai, Remote clients",
  },
];

// Nuanced business idea generator function
function generateIdea({ skills = "", capital = "", type = "", audience = "" }) {
  skills = skills.trim().toLowerCase();
  capital = capital.trim().toLowerCase();
  type = type.trim().toLowerCase();
  audience = audience.trim();

  let idea = "";
  let tagline = "";

  // Personalized logic
  if (skills.includes("design")) {
    idea = `Launch a boutique creative agency specializing in digital branding and user-centered web design for ${audience || "emerging businesses"}.`;
    tagline = "You could focus on helping small brands transform their online presence with modern aesthetics and UX best practices.";
  } else if (skills.includes("coding") || skills.includes("development")) {
    idea = `Build and sell tailored web apps or SaaS tools for ${audience || "niche industries"}, solving real workflow problems.`;
    tagline = "Start by identifying recurring pain points in your target segment and develop MVP solutions for them.";
  } else if (skills.includes("teaching") || skills.includes("training")) {
    idea = `Create an interactive online academy for ${audience || "young professionals"}, delivering micro-courses with mentorship.`;
    tagline = "By combining personalized feedback and community, you offer more value than mass-market platforms.";
  } else if (skills.includes("marketing") || skills.includes("social media")) {
    idea = `Start a results-driven social media management service for ${audience || "local shops and startups"}, focused on content that converts.`;
    tagline = "Offer analytics-driven strategies and Instagram-first campaigns that generate real leads.";
  } else if (skills.includes("food") || skills.includes("cooking")) {
    idea = `Launch a cloud kitchen or meal prep delivery for ${audience || "busy professionals"} using unique, healthy recipes.`;
    tagline = "Start small from home, leverage WhatsApp and Instagram for orders, and partner with local fitness trainers for credibility.";
  } else if (skills.includes("finance") || skills.includes("accounting")) {
    idea = `Offer virtual GST, accounting, and compliance services for ${audience || "freelancers and solopreneurs"}.`;
    tagline = "Focus on affordable, subscription-based plans and quick WhatsApp support for clients.";
  } else if (type.includes("online")) {
    idea = `Curate a specialized e-commerce store for ${audience || "hobbyists"}, featuring curated or handmade products.`;
    tagline = "Niche stores win by serving passionate communities with limited-edition goods.";
  } else {
    idea = `Spot an unmet need for ${audience ? audience : "your local community"} and design a unique ${type || "service"} that leverages your interests.`;
    tagline = "Start with market research—look for problems people complain about but no one's solving, then prototype a solution.";
  }

  // Capital-based nuance
  if (capital.includes("low") || capital === "0") {
    tagline += " Start lean: use digital tools, WhatsApp, and word-of-mouth before investing in ads or staff.";
  } else if (capital.includes("high")) {
    tagline += " With higher capital, consider hiring experts, paid marketing, and building a strong initial brand identity.";
  }

  return (
    <div>
      <div style={{
        fontWeight: 600,
        fontSize: 17,
        marginBottom: 10,
        color: "#1e293b"
      }}>
        {idea}
      </div>
      <div style={{
        fontSize: 15,
        color: "#475569",
        background: "#f1f5f9",
        borderRadius: 8,
        padding: 10,
      }}>
        {tagline}
      </div>
    </div>
  );
}

export default function App() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({});
  const [idea, setIdea] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);

  const handleNext = () => {
    if (step < questions.length - 1) setStep((s) => s + 1);
    else {
      setIdea(generateIdea(form));
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [questions[step].name]: e.target.value });
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(135deg,#e0e7ff 0%,#fff 50%,#e9d5ff 100%)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: 24
    }}>
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        style={{ maxWidth: 480, width: "100%" }}
      >
        {/* Logo */}
        <div style={{ width: "100%", display: "flex", justifyContent: "center", marginBottom: 24 }}>
          <img
            src="/logo.png"
            alt="Srikrishna Solutions Logo"
            style={{ maxWidth: 120, borderRadius: 18, boxShadow: "0 2px 12px #e0e7ff" }}
          />
        </div>
        <div style={{
          background: "#fff",
          borderRadius: 20,
          boxShadow: "0 4px 32px #e0e0e0",
          padding: 32
        }}>
          <h1 style={{ fontSize: 28, fontWeight: "bold", marginBottom: 16, textAlign: "center" }}>
            Business Idea Generator
          </h1>
          <p style={{ textAlign: "center", marginBottom: 20, color: "#6366f1" }}>
            Answer a few quick questions and get a customized business idea. Need expert help?{" "}
            <span
              style={{ color: "#3b82f6", textDecoration: "underline", cursor: "pointer" }}
              onClick={() => setContactOpen(true)}
            >
              Contact Srikrishna Solutions
            </span>
            .
          </p>

          {!idea ? (
            <>
              <label style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>
                {questions[step].label}
              </label>
              <input
                placeholder={questions[step].placeholder}
                value={form[questions[step].name] || ""}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  marginBottom: 16,
                  borderRadius: 8,
                  border: "1px solid #c7d2fe",
                  outline: "none",
                  fontSize: 16
                }}
              />
              <button
                onClick={handleNext}
                style={{
                  width: "100%",
                  padding: 12,
                  background: "#6366f1",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  fontWeight: 600,
                  fontSize: 16,
                  cursor: "pointer"
                }}
              >
                {step === questions.length - 1 ? "Get Idea" : "Next"}
              </button>
            </>
          ) : (
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
            >
              <div style={{
                background: "#e0e7ff",
                padding: 16,
                borderRadius: 12,
                marginBottom: 20,
                fontSize: 18,
                textAlign: "center"
              }}>
                <span style={{ fontWeight: 600, color: "#1e293b" }}>Your Business Idea:</span>
                <br />
                <div style={{ marginTop: 10 }}>{idea}</div>
              </div>
              <div style={{ marginBottom: 16, width: "100%", textAlign: "center" }}>
                <div style={{ fontSize: 15, color: "#6b7280", marginBottom: 12 }}>
                  Want help with <b>Research, Consultation, GST, Web Design, or Social Media Management?</b>
                </div>
                <a
                  href="https://wa.me/918328638078?text=Hi%20Srikrishna%20Solutions%2C%20I%20am%20interested%20in%20your%20services."
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-block",
                    width: "100%",
                    padding: "12px 0",
                    background: "#25D366",
                    color: "#fff",
                    borderRadius: 8,
                    fontWeight: 600,
                    fontSize: 17,
                    textDecoration: "none",
                    boxShadow: "0 2px 6px rgba(39, 174, 96, 0.09)"
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width={22}
                    height={22}
                    fill="white"
                    style={{ verticalAlign: 'middle', marginRight: 8, marginBottom: 3 }}
                    viewBox="0 0 448 512"
                  >
                    <path d="M380.9 97.1C339-4.5 232.3-35.3 140.1 18.6 47.8 72.6 17 179.3 60.1 271.6L8.2 443.8c-5.5 18.2 11.8 35.6 30 30l172.2-51.9c92.3 43.1 198.9 12.2 252.9-80C483.5 232.2 452.7 125.5 380.9 97.1zM232 372.7c-86.7 0-157.1-70.4-157.1-157.1S145.3 58.5 232 58.5 389.1 128.9 389.1 215.6 318.7 372.7 232 372.7zm83.5-115.6c-4.1-2.1-24.1-11.9-27.8-13.3-3.7-1.4-6.4-2.1-9.1 2.1-2.6 4.1-10.4 13.3-12.8 16-2.4 2.6-4.7 2.9-8.8 1-24.1-12.1-39.8-21.6-55.9-48.8-4.2-7.2 4.2-6.7 12-22.2 1.3-2.6.7-4.9-.4-6.9-1-2.1-9.1-22-12.5-30.1-3.2-7.7-6.6-6.6-9.1-6.8-2.4-.2-5.1-.2-7.7-.2s-7.1 1-10.8 5c-3.7 4.1-14.1 13.8-14.1 33.7s14.5 39.1 16.5 41.9c2 2.8 28.5 43.6 69.3 59.2 9.7 3.8 17.2 6 23.1 7.6 9.7 2.6 18.6 2.2 25.6 1.3 7.8-1 24.1-9.9 27.5-19.5 3.4-9.6 3.4-17.9 2.4-19.5-1-1.5-3.7-2.4-7.7-4.4z"/>
                  </svg>
                  Contact Srikrishna Solutions on WhatsApp
                </a>
              </div>
              <button
                onClick={() => {
                  setStep(0); setForm({}); setIdea(null);
                }}
                style={{
                  background: "none",
                  border: "none",
                  color: "#6366f1",
                  fontWeight: 600,
                  fontSize: 16,
                  cursor: "pointer",
                  marginTop: 8
                }}
              >
                Generate another idea
              </button>
            </motion.div>
          )}
        </div>

        {/* Contact Modal */}
        {contactOpen && (
          <div style={{
            position: "fixed",
            top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10
          }}>
            <div
              style={{
                background: "#fff",
                borderRadius: 20,
                boxShadow: "0 8px 40px #c7d2fe",
                padding: 32,
                position: "relative",
                maxWidth: 400,
                width: "100%"
              }}
            >
              <button
                onClick={() => { setContactOpen(false); }}
                style={{
                  position: "absolute",
                  right: 24,
                  top: 16,
                  color: "#888",
                  fontWeight: "bold",
                  border: "none",
                  background: "none",
                  fontSize: 18,
                  cursor: "pointer"
                }}
                aria-label="Close"
              >✕</button>
              <div style={{ textAlign: "center" }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>
                  Contact Srikrishna Solutions
                </h2>
                <p style={{ marginBottom: 20 }}>
                  Want help with <b>Research, Consultation, GST, Web Design, or Social Media Management</b>?<br />
                  Message us directly on WhatsApp:
                </p>
                <a
                  href="https://wa.me/918328638078?text=Hi%20Srikrishna%20Solutions%2C%20I%20am%20interested%20in%20your%20services."
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-block",
                    marginTop: 8,
                    padding: "12px 24px",
                    background: "#25D366",
                    color: "#fff",
                    borderRadius: 8,
                    fontWeight: 600,
                    fontSize: 16,
                    textDecoration: "none",
                    boxShadow: "0 2px 6px rgba(39, 174, 96, 0.12)"
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width={22}
                    height={22}
                    fill="white"
                    style={{ verticalAlign: 'middle', marginRight: 8, marginBottom: 3 }}
                    viewBox="0 0 448 512"
                  >
                    <path d="M380.9 97.1C339-4.5 232.3-35.3 140.1 18.6 47.8 72.6 17 179.3 60.1 271.6L8.2 443.8c-5.5 18.2 11.8 35.6 30 30l172.2-51.9c92.3 43.1 198.9 12.2 252.9-80C483.5 232.2 452.7 125.5 380.9 97.1zM232 372.7c-86.7 0-157.1-70.4-157.1-157.1S145.3 58.5 232 58.5 389.1 128.9 389.1 215.6 318.7 372.7 232 372.7zm83.5-115.6c-4.1-2.1-24.1-11.9-27.8-13.3-3.7-1.4-6.4-2.1-9.1 2.1-2.6 4.1-10.4 13.3-12.8 16-2.4 2.6-4.7 2.9-8.8 1-24.1-12.1-39.8-21.6-55.9-48.8-4.2-7.2 4.2-6.7 12-22.2 1.3-2.6.7-4.9-.4-6.9-1-2.1-9.1-22-12.5-30.1-3.2-7.7-6.6-6.6-9.1-6.8-2.4-.2-5.1-.2-7.7-.2s-7.1 1-10.8 5c-3.7 4.1-14.1 13.8-14.1 33.7s14.5 39.1 16.5 41.9c2 2.8 28.5 43.6 69.3 59.2 9.7 3.8 17.2 6 23.1 7.6 9.7 2.6 18.6 2.2 25.6 1.3 7.8-1 24.1-9.9 27.5-19.5 3.4-9.6 3.4-17.9 2.4-19.5-1-1.5-3.7-2.4-7.7-4.4z"/>
                  </svg>
                  Contact Srikrishna Solutions on WhatsApp
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Footer with Disclaimer */}
        <footer style={{ marginTop: 32, textAlign: "center", fontSize: 13, color: "#a3a3a3" }}>
          <div>
            © 2025 Srikrishna Solutions. All rights reserved.
          </div>
          <div style={{ marginTop: 14, fontSize: 12, color: "#f59e42", background: "#fff7ed", padding: "10px 20px", borderRadius: 12, display: "inline-block" }}>
            <b>Disclaimer:</b> This tool provides business suggestions for informational purposes only. The ideas may be inaccurate or incomplete. Please conduct your own research and due diligence before making any decisions.
          </div>
        </footer>
      </motion.div>

      {/* Floating WhatsApp Button */}
      <a
        href="https://wa.me/918328638078?text=Hi%20Srikrishna%20Solutions%2C%20I%20am%20interested%20in%20your%20services."
        target="_blank"
        rel="noopener noreferrer"
        style={{
          position: "fixed",
          bottom: 24,
          right: 24,
          zIndex: 1000,
          background: "#25D366",
          borderRadius: "50%",
          width: 60,
          height: 60,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 4px 20px rgba(39,174,96,0.15)",
          transition: "transform 0.15s",
          cursor: "pointer",
        }}
        aria-label="Chat with us on WhatsApp"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={32}
          height={32}
          fill="white"
          viewBox="0 0 448 512"
        >
          <path d="M380.9 97.1C339-4.5 232.3-35.3 140.1 18.6 47.8 72.6 17 179.3 60.1 271.6L8.2 443.8c-5.5 18.2 11.8 35.6 30 30l172.2-51.9c92.3 43.1 198.9 12.2 252.9-80C483.5 232.2 452.7 125.5 380.9 97.1zM232 372.7c-86.7 0-157.1-70.4-157.1-157.1S145.3 58.5 232 58.5 389.1 128.9 389.1 215.6 318.7 372.7 232 372.7zm83.5-115.6c-4.1-2.1-24.1-11.9-27.8-13.3-3.7-1.4-6.4-2.1-9.1 2.1-2.6 4.1-10.4 13.3-12.8 16-2.4 2.6-4.7 2.9-8.8 1-24.1-12.1-39.8-21.6-55.9-48.8-4.2-7.2 4.2-6.7 12-22.2 1.3-2.6.7-4.9-.4-6.9-1-2.1-9.1-22-12.5-30.1-3.2-7.7-6.6-6.6-9.1-6.8-2.4-.2-5.1-.2-7.7-.2s-7.1 1-10.8 5c-3.7 4.1-14.1 13.8-14.1 33.7s14.5 39.1 16.5 41.9c2 2.8 28.5 43.6 69.3 59.2 9.7 3.8 17.2 6 23.1 7.6 9.7 2.6 18.6 2.2 25.6 1.3 7.8-1 24.1-9.9 27.5-19.5 3.4-9.6 3.4-17.9 2.4-19.5-1-1.5-3.7-2.4-7.7-4.4z"/>
        </svg>
      </a>
    </div>
  );
}
